import { v4 as uuidv4 } from 'uuid';
import { CanvasEx } from '@/typings/canvas';
import { BaseCanvasElement } from '@/typings/customilyElements/canvasElements';
import { ElementsGroup, GroupType } from '@/typings/customilyElements/elementsGroup';

export const createGroup = (
    canvas: CanvasEx,
    options: {
        type: GroupType;
        zIndex: number;
        parentId: string;
        name?: string;
        spotifyPlayer?: boolean;
    }
): ElementsGroup => {
    const groups = canvas.getElementsGroups() || [];
    const maxPosition = Math.max(...groups.map((g) => g.position), 0);
    const layout = {
        id: uuidv4(),
        uuid: uuidv4(),
        locked: false,
        visible: true,
        name: options.name || 'Layout',
        type: options.type,
        pageEpsId: canvas.getFirstActivePageEpsId(),
        zIndex: options.zIndex,
        position: maxPosition + 1,
        parentId: options.parentId,
        SpotifyPlayer: options.spotifyPlayer || undefined
        // check
        // position: props.allowPositionGroupEdit ? canvas?.getMaxPositionGroup() || 0 : 0
    };

    return layout;
};

export const setGroupLock = (group: ElementsGroup, canvas: CanvasEx, locked: boolean) => {
    if (!group) return;
    group.locked = locked;
    const elements: BaseCanvasElement[] = canvas.getGroupElements(group);
    for (const element of elements) {
        element.set('dirty', true);
        canvas.fire('object:modified', {
            target: element
        });
    }
    const childGroupsFound = canvas.getElementsGroups().filter((g) => g.parentId === group.id);
    if (!childGroupsFound?.length) {
        canvas.requestRenderAll();
        return;
    }
    for (const childGroup of childGroupsFound) {
        setGroupLock(childGroup, canvas, locked);
    }
    canvas.requestRenderAll();
};

const findGroupInCanvas = (group: ElementsGroup, canvasGroups: ElementsGroup[]): ElementsGroup | undefined => {
    return canvasGroups.find((g) => g.id === group.id) || canvasGroups.find((g) => g.uuid === group.uuid);
};

export const setGroupVisibility = (groups: ElementsGroup[], canvas: CanvasEx, visible: boolean): void => {
    try {
        if (!groups || groups.length === 0) return;

        const allGroups = canvas.getElementsGroups();
        const visibleGroupIds = new Set<string>();

        const validGroups: ElementsGroup[] = [];
        for (const group of groups) {
            const foundGroup = findGroupInCanvas(group, allGroups);
            if (foundGroup) {
                validGroups.push(foundGroup);
            }
        }

        if (validGroups.length === 0) {
            console.warn('No valid groups found in canvas');
            return;
        }

        for (const group of validGroups) {
            updateGroupVisibility(group, canvas, visible);
            setGroupLock(group, canvas, !visible);
            visibleGroupIds.add(group.id);

            const parentGroup = allGroups.find((g) => g.id === group.parentId);
            if (parentGroup) {
                updateGroupVisibility(parentGroup, canvas, visible);
                setGroupLock(parentGroup, canvas, !visible);
                visibleGroupIds.add(parentGroup.id);

                const childGroups = allGroups.filter((g) => g.parentId === parentGroup.id);
                childGroups.forEach((childGroup) => {
                    if (!visibleGroupIds.has(childGroup.id)) {
                        updateGroupVisibility(childGroup, canvas, false);
                        setGroupLock(childGroup, canvas, true);
                    }
                });
            }
        }

        allGroups.forEach((g) => {
            if (!visibleGroupIds.has(g.id)) {
                updateGroupVisibility(g, canvas, false);
                setGroupLock(g, canvas, true);
            }
        });

        canvas.requestRenderAll();
    } catch (error) {
        console.error('An error occurred trying to set the group visibility', error);
    }
};

const updateGroupVisibility = (group: ElementsGroup, canvas: CanvasEx, visible: boolean): void => {
    group.visible = visible;
    const elements: BaseCanvasElement[] = canvas.getGroupElements(group);
    elements.forEach((element) => element.set('dirty', true));
};
